import { sendMessageToIframe } from "../utils/messaging";
import { MessageType } from "../types/page-extraction.types";
import SimplePageExtraction from "../helpers/page-extraction";

// Configuration constants
const IFRAME_ID = "automation-embed-iframe";
const AUTOMATION_WRAPPER_ID = "automation-embed-wrapper";
const AUTOMATION_CHATBUBBLE_ID = "automation-chatbubble-button";

const visible = `${AUTOMATION_WRAPPER_ID}-visible`;
const hidden = `${AUTOMATION_WRAPPER_ID}-hidden`;
const mobileViewport = `${AUTOMATION_WRAPPER_ID}-mobile`;

// SVG Icons
const ChevronDown = `
<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-chevron-down"><path d="m6 9 6 6 6-6"/></svg>
`;

const MessagesSquare = `
<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-messages-square"><path d="M14 9a2 2 0 0 1-2 2H6l-4 4V4c0-1.1.9-2 2-2h8a2 2 0 0 1 2 2z"/><path d="M18 9h2a2 2 0 0 1 2 2v11l-4-4h-6a2 2 0 0 1-2-2v-1"/></svg>
`;

// Widget configuration interface
interface WidgetConfig {
  brand_color: string;
  text_color: string;
  agentId: string;
  [key: string]: any;
}

// Widget options interface
interface WidgetOptions {
  agentId?: string;
  brand_color?: string;
  text_color?: string;
  [key: string]: any;
}

class AutomationWidget {
  private iframeOpen: boolean = false;
  private widgetConfig: WidgetConfig;
  private iframe!: HTMLIFrameElement;
  private pageExtractor: SimplePageExtraction;
  constructor(options: WidgetOptions = {}) {
    this.widgetConfig = {
      brand_color: "#007bff",
      text_color: "#fff",
      agentId: options.agentId || "demo-agent",
      ...options,
    };

    this.pageExtractor = new SimplePageExtraction({
      includeInvisible: false,
    });

    // Initialize immediately
    this.init();
  }

  // Initialize the widget
  private async init(): Promise<void> {
    this.injectCss();
    this.initializeWorkflowWidgetIframe();
    this.mountIframe();

    // Add event listeners
    window.addEventListener("resize", () => this.handleResponsiveness(), false);
    window.addEventListener(
      "DOMContentLoaded",
      () => this.handleResponsiveness(),
      false
    );
  }

  // Get agent ID
  public getAgentId(): string {
    return this.widgetConfig.agentId;
  }

  // Mount the iframe to the DOM
  private mountIframe(): void {
    if (!document.getElementById(IFRAME_ID)) {
      window.addEventListener("message", this.receiveMessage.bind(this), false);

      // Create wrapper
      const wrapper = document.createElement("div");
      wrapper.id = AUTOMATION_WRAPPER_ID;
      wrapper.setAttribute(
        "class",
        `${AUTOMATION_WRAPPER_ID} ${AUTOMATION_WRAPPER_ID}-hidden`
      );

      // Create close button
      const closeButton = document.createElement("button");
      closeButton.className = `${AUTOMATION_WRAPPER_ID}-close`;
      closeButton.innerHTML = "×";
      closeButton.onclick = (e: Event) => {
        e.stopPropagation();
        this.toggleIframe();
      };

      // Assemble the components
      wrapper.appendChild(closeButton);
      wrapper.appendChild(this.iframe);
      document.body.appendChild(wrapper);

      // Create chat bubble button
      this.createChatBubble();
    }
  }

  // Initialize the iframe element
  private initializeWorkflowWidgetIframe(): void {
    const { clientUrl } = this.getScriptConfig();

    this.iframe = document.createElement("iframe");
    this.iframe.id = IFRAME_ID;
    this.iframe.src = clientUrl + "/chat";
    this.iframe.style.cssText = `
        border: none;
        width: 100%;
        height: 100%;
        z-index: ${Number.MAX_SAFE_INTEGER};
        background: white;
    `;
  }

  // Handle incoming messages from the iframe
  private receiveMessage(event: MessageEvent): void {
    if (event.source !== this.iframe?.contentWindow) return;

    const { type, data } = event.data;

    switch (type) {
      case "widget-ready":
        this.sendMessageToClient("parent-ready");
        break;
      case "page-metadata":
        this.handlePageMetadataRequest(data);
        break;
      case "request-platform-id":
        this.handlePlatformIdRequest(data);
        break;
      case "OPEN_WIDGET":
        this.toggleIframe();
        break;
      case "CLOSE_WIDGET":
        this.closeIframe();
        break;
      case "REQUEST_NAVIGATION_DATA":
        this.handleNavigationDataRequest(data);
        break;
      case "CLEAR_NAVIGATION_FLAGS":
        this.handleClearNavigationFlags();
        break;
      default:
        console.log("Unhandled message type:", type);
        break;
    }
  }

  private handlePageMetadataRequest(data: any): void {
    const pageMetadata = this.pageExtractor.extractCurrentPageMetadata();
    this.sendMessageToClient("page-metadata", {
      pageSnapshot: pageMetadata,
      requestId: data.requestId,
    });
  }

  private handleNavigationDataRequest(data: any): void {
    // Get navigation data from session storage
    const navigated = sessionStorage.getItem("page_navigated");
    const timestamp = sessionStorage.getItem("navigation_timestamp");
    const url = sessionStorage.getItem("navigation_url");
    const type = sessionStorage.getItem("navigation_type");
    const source = sessionStorage.getItem("navigation_source");

    const navigationData = {
      hasNavigated: navigated === "true",
      timestamp: timestamp ? parseInt(timestamp) : undefined,
      url: url || undefined,
      type: type || undefined,
      source: source || undefined,
    };

    // Send navigation data back to client with requestId
    this.sendMessageToClient("NAVIGATION_DATA", {
      ...navigationData,
      requestId: data?.requestId,
    });
  }

  private handleClearNavigationFlags(): void {
    // Clear navigation flags from session storage
    sessionStorage.removeItem("page_navigated");
    sessionStorage.removeItem("navigation_timestamp");
    sessionStorage.removeItem("navigation_url");
    sessionStorage.removeItem("navigation_type");
    sessionStorage.removeItem("navigation_source");

    console.log("Navigation flags cleared");
  }

  // Send message to iframe using the imported messaging utility
  private sendMessageToClient(type: MessageType, data: any = {}): boolean {
    return sendMessageToIframe(type, data);
  }

  // Toggle iframe visibility
  public toggleIframe(): void {
    const wrapper = document.getElementById(AUTOMATION_WRAPPER_ID);
    const chatBubble = document.getElementById(AUTOMATION_CHATBUBBLE_ID);

    if (!wrapper || !chatBubble) return;

    if (this.iframeOpen || wrapper.classList.contains(visible)) {
      // Close the iframe
      wrapper.classList.remove(visible);
      wrapper.classList.remove(mobileViewport);
      wrapper.classList.add(hidden);

      // Update chat bubble appearance
      chatBubble.style.animation = "chatBubblePulse 2s infinite";
      chatBubble.innerHTML = `
        <div style="display: flex; align-items: center; justify-content: center; width: 100%; height: 100%;">
          ${MessagesSquare}
        </div>
      `;

      this.iframeOpen = false;
      this.handleResponsiveness();
    } else {
      // Open the iframe
      wrapper.classList.remove(hidden);
      wrapper.classList.add(visible);

      // Update chat bubble appearance
      chatBubble.style.animation = "none";
      chatBubble.innerHTML = `
        <div style="display: flex; align-items: center; justify-content: center; width: 100%; height: 100%;">
          ${ChevronDown}
        </div>
      `;

      this.iframeOpen = true;
      this.handleResponsiveness();

      // Send widget-opened event to iframe
      setTimeout(() => {
        this.sendMessageToClient("WIDGET_OPENED");
      }, 100);
    }
  }

  // Close iframe (called when workflow is completed or failed)
  public closeIframe(): void {
    const wrapper = document.getElementById(AUTOMATION_WRAPPER_ID);
    const chatBubble = document.getElementById(AUTOMATION_CHATBUBBLE_ID);

    if (!wrapper || !chatBubble) return;

    // Close the iframe
    wrapper.classList.remove(visible);
    wrapper.classList.remove(mobileViewport);
    wrapper.classList.add(hidden);

    // Update chat bubble appearance
    chatBubble.style.animation = "chatBubblePulse 2s infinite";
    chatBubble.innerHTML = `
      <div style="display: flex; align-items: center; justify-content: center; width: 100%; height: 100%;">
        ${MessagesSquare}
      </div>
    `;

    this.iframeOpen = false;
    this.handleResponsiveness();
  }

  // Create chat bubble button
  private createChatBubble(): void {
    if (!document.getElementById(AUTOMATION_CHATBUBBLE_ID)) {
      const chatBubble = document.createElement("button");
      chatBubble.id = AUTOMATION_CHATBUBBLE_ID;

      // Use the MessagesSquare icon for better visual appeal
      chatBubble.innerHTML = `
        <div style="display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; position: relative;">
          ${MessagesSquare}
          <div class="chat-bubble-notification" style="display: none;"></div>
        </div>
      `;

      chatBubble.onclick = () => this.toggleIframe();
      chatBubble.title = "Open AI Assistant";

      // Add initial animation
      chatBubble.style.animation = "chatBubblePulse 2s infinite";

      // Ensure positioning is applied immediately
      chatBubble.style.position = "fixed";
      chatBubble.style.bottom = "2rem";
      chatBubble.style.right = "2rem";
      chatBubble.style.zIndex = (Number.MAX_SAFE_INTEGER - 2).toString();

      document.body.appendChild(chatBubble);

      // Show notification dot after a delay to draw attention
      setTimeout(() => {
        const notificationDot = chatBubble.querySelector(
          ".chat-bubble-notification"
        );
        if (notificationDot) {
          (notificationDot as HTMLElement).style.display = "block";
        }
      }, 3000);
    }
  }

  // Handle responsive behavior
  private handleResponsiveness(): void {
    const wrapper = document.getElementById(AUTOMATION_WRAPPER_ID);
    const chatBubble = document.getElementById(AUTOMATION_CHATBUBBLE_ID);

    if (!wrapper || !chatBubble) return;

    const isMobile = window.innerWidth <= 768;

    if (this.iframeOpen) {
      if (isMobile) {
        wrapper.classList.add(mobileViewport);
      } else {
        wrapper.classList.remove(mobileViewport);
      }
    }
  }

  // Inject CSS styles
  private injectCss(): void {
    const style = document.createElement("style");
    style.innerHTML = `
        .${AUTOMATION_WRAPPER_ID}{
            width: 0px;
            height: 0px;
            position: fixed;
            bottom: 6rem;
            right: 2rem;
            border-radius: 12px;
            overflow: hidden;
            border: 1px solid #e0e0e0;
            transition: all 0.3s ease-in-out;
            z-index: ${Number.MAX_SAFE_INTEGER};
            opacity: 0;
            visibility: hidden;
            background: white;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        }

        .${AUTOMATION_WRAPPER_ID}-hidden {
            width: 0px;
            height: 0px;
            opacity: 0;
            visibility: hidden;
        }

        .${AUTOMATION_WRAPPER_ID}-visible {
            width: 450px;
            height: 700px;
            opacity: 1;
            visibility: visible;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
            overflow: hidden;
        }

        .${AUTOMATION_WRAPPER_ID}-mobile {
            width: 100vw;
            height: 100vh;
            bottom: 0;
            right: 0;
            border-radius: 0;
            overflow: hidden;
        }

        #automation-chatbubble-button {
            position: fixed;
            bottom: -5rem;
            right: 2rem;
            width: 40px;
            height: 40px;
            border-radius: 50%;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border: none;
            display: flex;
            justify-content: center;
            align-items: center;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
            cursor: pointer;
            transition: all 0.3s ease;
            z-index: ${Number.MAX_SAFE_INTEGER - 2};
            font-size: 24px;
            position: relative; /* For notification dot positioning */
        }

        #automation-chatbubble-button:hover {
            transform: scale(1.1);
            box-shadow: 0 6px 25px rgba(0, 0, 0, 0.3);
        }

        #automation-chatbubble-button:active {
            transform: scale(0.95);
        }

        /* Chat bubble pulse animation */
        @keyframes chatBubblePulse {
            0% {
                transform: scale(1);
                box-shadow: 0 4px 20px rgba(102, 126, 234, 0.3);
            }
            50% {
                transform: scale(1.05);
                box-shadow: 0 6px 25px rgba(102, 126, 234, 0.4);
            }
            100% {
                transform: scale(1);
                box-shadow: 0 4px 20px rgba(102, 126, 234, 0.3);
            }
        }

        #automation-chatbubble-button svg {
            width: 24px;
            height: 24px;
            color: white;
        }

        /* Mobile responsiveness for chat bubble */
        @media (max-width: 768px) {
            #automation-chatbubble-button {
                width: 40px;
                height: 40px;
                bottom: 1rem;
                right: 1rem;
            }

            #automation-chatbubble-button svg {
                width: 24px;
                height: 24px;
            }
        }

        /* Add a subtle notification dot */
        .chat-bubble-notification {
            position: absolute;
            top: -2px;
            right: -2px;
            width: 12px;
            height: 12px;
            background: #ff4757;
            border-radius: 50%;
            border: 2px solid white;
            animation: notificationPulse 2s infinite;
        }

        @keyframes notificationPulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.2); }
            100% { transform: scale(1); }
        }

        /* Close button */
        .${AUTOMATION_WRAPPER_ID}-close {
            position: absolute;
            top: 10px;
            right: 10px;
            width: 24px;
            height: 24px;
            border-radius: 50%;
            background: #f0f0f0;
            color: #666;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 14px;
            z-index: ${Number.MAX_SAFE_INTEGER + 1};
            transition: all 0.2s ease;
        }

        .${AUTOMATION_WRAPPER_ID}-close:hover {
            background: #e0e0e0;
            color: #333;
            transform: scale(1.1);
        }
    `;
    document.head.appendChild(style);
  }

  // Page Mapping Methods (Optional - only if page mapping is available)
  /**
   * Build page mapping from current page (if page mapping is available)
   */
  public async buildPageMapping(): Promise<any> {
    // Check if page mapping is available
    if (typeof window !== "undefined" && (window as any).pageMapping) {
      return (window as any).pageMapping.buildCurrentPageMapping();
    }
    throw new Error(
      "Page mapping is not available. Load the page mapping script first."
    );
  }

  /**
   * Build page mapping with custom options (if page mapping is available)
   */
  public async buildPageMappingWithOptions(options: {
    includeInvisible?: boolean;
    includeDisabled?: boolean;
    viewportOnly?: boolean;
    minSize?: { width: number; height: number };
  }): Promise<any> {
    // Check if page mapping is available
    if (typeof window !== "undefined" && (window as any).pageMapping) {
      return (window as any).pageMapping.buildCurrentPageMappingWithOptions(
        options
      );
    }
    throw new Error(
      "Page mapping is not available. Load the page mapping script first."
    );
  }

  /**
   * Update page mapping configuration (if page mapping is available)
   */
  public updatePageMappingOptions(options: {
    autoMapOnNavigation?: boolean;
    autoMapOnDOMChanges?: boolean;
    domChangeDelay?: number;
    includeInvisible?: boolean;
    includeDisabled?: boolean;
    minSize?: { width: number; height: number };
  }) {
    // Check if page mapping is available
    if (typeof window !== "undefined" && (window as any).pageMapping) {
      (window as any).pageMapping.updateOptions(options);
    } else {
      console.warn(
        "Page mapping is not available. Load the page mapping script first."
      );
    }
  }

  /**
   * Enable or disable automatic page mapping (if page mapping is available)
   */
  public setAutoPageMapping(enabled: boolean) {
    // Check if page mapping is available
    if (typeof window !== "undefined" && (window as any).pageMapping) {
      (window as any).pageMapping.setAutoMapping(enabled);
    } else {
      console.warn(
        "Page mapping is not available. Load the page mapping script first."
      );
    }
  }

  /**
   * Get current page extraction data
   */
  public getCurrentPageData() {
    return this.pageExtractor.extractCurrentPageMetadata();
  }

  private getScriptConfig(): { platformId: string; clientUrl: string } {
    const scriptTag = document.querySelector(
      "script[data-platform-id]"
    ) as HTMLScriptElement;

    const platformId =
      scriptTag?.getAttribute("data-platform-id") || "demo-platform";
    const clientUrl =
      scriptTag?.getAttribute("data-client") || "http://localhost:2026";

    return { platformId, clientUrl };
  }

  private handlePlatformIdRequest(data: any): void {
    const { platformId } = this.getScriptConfig();

    this.sendMessageToClient("platform-id", {
      platformId,
      requestId: data?.requestId,
    });
  }
}

// Create and export the automation widget instance
const automation = new AutomationWidget();

export default automation;
